import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Printer, FileDown, X, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { PeriodChips, reportPrint, reportPdf, PENDING_PERIODS } from "./reportShared";

function SummaryTable({ title, rows, onOpen, prefix }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-200 font-head font-semibold">{title}</div>
      <div className="overflow-x-auto"><table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200"><tr>
          <th className="th">{title.startsWith("Product") ? "Product" : "Category"}</th>
          <th className="th">Pending Orders</th><th className="th">Total Qty</th><th className="th">OA Numbers</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 && <tr><td className="td text-slate-400 text-center" colSpan={4} data-testid={`${prefix}-empty`}>No pending orders for this period.</td></tr>}
          {rows.map((g, i) => (
            <tr key={g.key} className="hover:bg-amber-50/50 cursor-pointer" onClick={() => onOpen(g)} data-testid={`${prefix}-row-${i}`}>
              <td className="td font-semibold text-slate-900">{g.key}</td>
              <td className="td">{g.count}</td>
              <td className="td">{g.qty}</td>
              <td className="td font-mono text-xs">{g.oaNumbers.join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  );
}

export default function PendingSummaryReport() {
  const [period, setPeriod] = useState("all");
  const [data, setData] = useState({ productWise: [], categoryWise: [], totalPendingOas: 0 });
  const [detail, setDetail] = useState(null); // { title, rows }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/pending-summary", { params: { period } });
      setData(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (kind, g) => {
    try {
      const { data } = await api.get("/reports/pending-orders", { params: { period } });
      setDetail({ title: `${kind}: ${g.key}`, rows: data.rows.filter((r) => g.oaIds.includes(r.oaId)) });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const periodLabel = PENDING_PERIODS.find((p) => p.key === period)?.label || period;

  return (
    <div className="space-y-4" data-testid="pending-summary-report">
      <div className="card p-4 flex flex-wrap items-center justify-between gap-3">
        <PeriodChips period={period} setPeriod={setPeriod} prefix="summary" />
        <div className="flex gap-2">
          <button className="btn-outline btn-sm" onClick={() => reportPrint(`/reports/pending-summary/html?period=${period}`)} data-testid="summary-print"><Printer className="h-4 w-4" /> Print</button>
          <button className="btn-outline btn-sm" onClick={() => reportPdf(`/reports/pending-summary/pdf?period=${period}`, `pending-summary-${period}.pdf`)} data-testid="summary-pdf"><FileDown className="h-4 w-4" /> Download PDF</button>
        </div>
      </div>

      <div className="card p-5 flex items-center gap-4 max-w-sm">
        <div className="h-12 w-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center"><Clock className="h-6 w-6" /></div>
        <div><div className="text-2xl font-head font-bold" data-testid="summary-total-oas">{data.totalPendingOas}</div><div className="text-xs text-slate-500 uppercase">Total Pending OAs ({periodLabel})</div></div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <SummaryTable title="Product-wise Pending Orders" rows={data.productWise} onOpen={(g) => openDetail("Product", g)} prefix="summary-product" />
        <SummaryTable title="Category-wise Pending Orders" rows={data.categoryWise} onOpen={(g) => openDetail("Category", g)} prefix="summary-category" />
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="card w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="summary-detail-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head text-xl font-bold">Pending OA Details — {detail.title}</h3>
              <button onClick={() => setDetail(null)} data-testid="summary-detail-close"><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="overflow-x-auto"><table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200"><tr>
                {["OA No.", "OA Date", "Customer", "Destination", "Product / Item", "Qty", "Pending Days", "Status"].map((h) => <th key={h} className="th">{h}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {detail.rows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50" data-testid={`summary-detail-row-${i}`}>
                    <td className="td font-mono font-semibold"><Link to={`/oa/${r.oaId}/details`} className="text-blue-700 hover:underline" onClick={() => setDetail(null)}>{r.oaNumber}</Link></td>
                    <td className="td">{r.oaDate || "—"}</td>
                    <td className="td">{r.customerName || "—"}</td>
                    <td className="td">{r.destination || "—"}</td>
                    <td className="td">{r.item}</td>
                    <td className="td">{r.qty}</td>
                    <td className="td">{r.pendingDays}d</td>
                    <td className="td">{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        </div>
      )}
    </div>
  );
}
